import React, { useState, useEffect } from 'react';
import { Box, TextField, InputAdornment, Badge, Checkbox, FormControlLabel, Tooltip, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ExportToExcel from '../ExportToExcel';

// Define theme colors for consistent styling
const themeColors = {
  primary: '#5757b0',
  primaryLight: '#7777d0',
  primaryDark: '#4545a0',
  accent: '#f59e0b',
  text: '#374151',
  lightText: '#6b7280',
  background: '#ffffff',
  lightBackground: '#f8faff',
};

// Styled Paper component for consistent look
const StyledPaper = ({ children, ...props }) => (
  <Paper
    {...props}
    sx={{
      borderRadius: '16px',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
      boxShadow: '0 8px 32px rgba(87, 87, 176, 0.12)',
      border: '1px solid rgba(87, 87, 176, 0.08)',
      padding: '16px',
      marginBottom: '16px',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 12px 40px rgba(87, 87, 176, 0.16)',
        transform: 'translateY(-2px)',
      },
      ...props.sx,
    }}
  >
    {children}
  </Paper>
);

const EventListHeader = ({
  filter,
  onFilterChange,
  onClearFilter,
  filteredTimeEvents,
  totalCount,
  showHidden,
  onCheckboxChange,
  hiddenEventCount,
  sortOrder,
  onToggleSortOrder,
  caseid,
  hideOtherTags = false,
  onHideOtherTagsChange
}) => {
  const { t } = useTranslation();
  const [expandedFilters, setExpandedFilters] = useState(false);
  
  // Use totalCount if provided, otherwise fall back to array length for backward compatibility
  const eventCount = totalCount !== undefined ? totalCount : filteredTimeEvents.length;

  useEffect(() => {
    const hasActiveFilters =
      filter ||
      showHidden;

    setExpandedFilters(hasActiveFilters);
  }, [filter, showHidden]);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Search Bar */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '24px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                border: '2px solid rgba(87, 87, 176, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(87, 87, 176, 0.3)',
                  boxShadow: '0 4px 20px rgba(87, 87, 176, 0.15)',
                },
                '&:focus-within': {
                  borderColor: themeColors.primary,
                  boxShadow: '0 0 0 4px rgba(87, 87, 176, 0.1)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                placeholder={t('eventlist.search_placeholder', 'Search by tags or data...')}
                value={filter}
                onChange={(e) => onFilterChange(e.target.value)}
                sx={{
                  '& .MuiInputBase-root': {
                    padding: '12px 20px',
                    fontSize: '1rem',
                    fontWeight: '500',
                    color: themeColors.text,
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(87, 87, 176, 0.6)',
                    opacity: 1
                  }
                }}
                InputProps={{
                  disableUnderline: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: themeColors.primary, mr: 1 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <Tooltip title={t('eventlist.document_count', { count: eventCount })}>
                      <InputAdornment position="end">
                        <Badge
                          badgeContent={eventCount}
                          color="primary"
                          max={9999}
                          sx={{ 
                            '& .MuiBadge-badge': { 
                              backgroundColor: themeColors.primary,
                              color: themeColors.lightText,
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            } 
                          }}
                        >
                          <InsertDriveFileIcon sx={{ color: themeColors.primary }} />
                        </Badge>
                      </InputAdornment>
                    </Tooltip>
                  ),
                }}
              />
            </Paper>
          </Box>
          
          {/* Export Button */}
          <ExportToExcel 
            data={filteredTimeEvents} 
            caseid={caseid} 
            fileName={t('eventlist.export_filename', 'event-list')} 
          />
        </Box>
      </StyledPaper>

      {/* Advanced Filters */}
      {expandedFilters && (
        <StyledPaper>
          {/* Show Hidden Events Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showHidden}
                  onChange={onCheckboxChange}
                  sx={{
                    color: themeColors.primary,
                    '&.Mui-checked': {
                      color: themeColors.primary,
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(87, 87, 176, 0.08)'
                    }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', fontSize: "0.85rem" }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: themeColors.text }}>
                    {t('eventlist.show_hidden_events', 'Show Hidden Events')}
                  </Typography>
                  {hiddenEventCount > 0 && (
                    <Badge
                      badgeContent={hiddenEventCount}
                      color="secondary"
                      max={9999}
                      sx={{ 
                        ml: 1, 
                        '& .MuiBadge-badge': { 
                          backgroundColor: themeColors.accent,
                          color: themeColors.lightText,
                          fontWeight: 'bold',
                          fontSize: '0.7rem'
                        } 
                      }}
                    />
                  )}
                </Box>
              }
            />
          </Box>
        </StyledPaper>
      )}

      {/* Sorting Controls Section */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Grouping Toggle */}
        </Box>
      </StyledPaper>
    </Box>
  );
};

export default EventListHeader;
